import { useEffect } from 'react';
import { Link } from '@tanstack/react-router';
import { ShieldOff, TriangleAlert } from 'lucide-react';
import { ApiError, messageFromError } from '@/lib/api';
import { posthog } from '@/lib/posthog';

// errorStatus extracts an HTTP status from either our hand-rolled ApiError or
// the raw Problem JSON the generated hey-api SDK throws under throwOnError.
export function errorStatus(e: unknown): number | undefined {
  if (e instanceof ApiError) return e.status;
  if (e && typeof e === 'object') {
    const s = (e as { status?: unknown }).status;
    if (typeof s === 'number') return s;
  }
  return undefined;
}

// AccessDenied — the friendly page-level state for a backend 403. The nav
// already hides destinations the user can't reach; this covers direct URLs,
// stale links, and permission changes mid-session.
export function AccessDenied() {
  return (
    <div className="mx-auto max-w-md py-16 text-center">
      <ShieldOff aria-hidden="true" className="mx-auto h-10 w-10 text-muted" />
      <h1 className="mt-4 font-serif text-xl font-bold text-[var(--text-heading)]">
        No access to this page
      </h1>
      <p className="mt-2 text-sm text-muted">
        Your role doesn't include the permission this page requires. If you
        think it should, ask an administrator.
      </p>
      <Link
        to="/"
        className="mt-6 inline-block rounded-[var(--radius-control)] border border-[var(--border-hairline)] px-4 py-2 text-sm font-medium hover:bg-[var(--surface-hover,rgba(0,0,0,0.04))]"
      >
        Back to Dashboard
      </Link>
    </div>
  );
}

// RouteErrorFallback — the router-level error boundary body: a friendly
// AccessDenied for 403s, a plain alert for everything else. Mounted as
// defaultErrorComponent, so it renders inside the app shell at the failing
// route's outlet.
export function RouteErrorFallback({ error }: { error: unknown }) {
  const status = errorStatus(error);
  const denied = status === 403;

  // As the app-wide default error sink this boundary must not swallow real
  // failures invisibly: log + capture everything that isn't a plain 403.
  useEffect(() => {
    if (denied) return;
    console.error('route error boundary:', error);
    posthog.capture?.('pms_route_error', {
      message: messageFromError(error),
      status,
      path: window.location.pathname,
    });
  }, [error, status, denied]);

  if (denied) return <AccessDenied />;
  return (
    <div
      role="alert"
      className="mx-auto mt-10 flex max-w-lg items-start gap-3 rounded-[var(--radius-control)] border border-[var(--ollio-danger)] bg-[var(--ollio-danger-bg)] px-4 py-3 text-sm text-destructive"
    >
      <TriangleAlert aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
      <span>{messageFromError(error)}</span>
    </div>
  );
}
